import { I18n } from "aws-amplify";
import { useHookstate } from "@hookstate/core";
import classNames from "classnames";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { globalShowCguState, ToastContext } from "../../../app-context";
import { getMailingTarget, saveMailingTarget } from "../../../service/ScrbService";
import { StructureContext } from "../../../structure-context";
import { isEmail } from "../../../Utils";

AddContactDialog.propTypes = {
    autoOpenIfEmpty: PropTypes.bool,
    visible: PropTypes.bool,
    onHide: PropTypes.func,
    onAdded: PropTypes.func,
    showNotif: PropTypes.func
};

export default function AddContactDialog(props) {
    const structureId = useContext(StructureContext).id;
    const toast = useContext(ToastContext);
    const showCgu = useHookstate(globalShowCguState).get();
    const [internalVisible, setInternalVisible] = useState(false);
    const [newMailingTarget, setNewMailingTarget] = useState({ structureId });

    const isVisible = (props.visible || internalVisible) && !showCgu;

    useEffect(() => {
        setNewMailingTarget({ structureId });
        if (!props.autoOpenIfEmpty || !structureId) {
            return;
        }
        getMailingTarget(structureId).then(targets => {
            if (!Array.isArray(targets) || targets.length === 0) {
                setInternalVisible(true);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [structureId, props.autoOpenIfEmpty]);

    const notify = (severity, summary, detail) => {
        if (props.showNotif) {
            props.showNotif(severity, summary, detail);
            return;
        }
        toast?.current?.show({
            severity,
            summary,
            detail,
            life: 3000
        });
    };

    const hide = () => {
        setInternalVisible(false);
        setNewMailingTarget({ structureId });
        if (props.onHide) {
            props.onHide();
        }
    };

    const onChangeMailingTarget = (e) => {
        setNewMailingTarget({ ...newMailingTarget, name: e.target.value });
    };

    const addMailingTarget = async () => {
        try {
            const res = await saveMailingTarget(newMailingTarget);
            if (res.statusCode === 200) {
                notify("success", I18n.get("success"), I18n.get("contactAdded"));
                if (props.onAdded) {
                    props.onAdded();
                }
                hide();
            } else {
                notify("error", I18n.get("unauthorized"), I18n.get("contactNotAdded"));
            }
        } catch (error) {
            console.error(error);
            notify("error", I18n.get("error"), I18n.get("contactNotAdded"));
        }
    };

    return (
        <Dialog
            header={I18n.get("addContact")}
            visible={isVisible}
            onHide={hide}
            className="flex flex-column w-10"
        >
            <Message className="mb-3" severity="info" text={I18n.get("infoContact")} />
            <InputText
                value={newMailingTarget.name || ""}
                onChange={onChangeMailingTarget}
                placeholder={I18n.get("addContact")}
                className={classNames({
                    "w-full mt-2 lg:mt-0": true,
                    "p-invalid": newMailingTarget.name && !isEmail(newMailingTarget.name)
                })}
            />
            <div className="flex flex-column lg:flex-row mt-2">
                <Button
                    label={I18n.get("add")}
                    icon="pi pi-plus"
                    onClick={addMailingTarget}
                    disabled={!newMailingTarget.name || !isEmail(newMailingTarget.name)}
                    outlined
                    className="w-full lg:w-10rem"
                />
                <Button
                    label={I18n.get("cancel")}
                    icon="pi pi-times"
                    onClick={hide}
                    outlined
                    className="w-full lg:w-10rem mt-2 lg:mt-0 lg:ml-2"
                />
            </div>
        </Dialog>
    );
}
